import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { CheckSmallCircleIcon } from '@zep-us/zus-icons';
import { useSpaceCategories } from '@zep/apis';
import { MapTemplate } from '@zep/types';
import { cn } from '@zep/utils';
interface TemplateCardProps {
  template: MapTemplate;
  onClick?: () => void;
  variant?: 'default' | 'panel' | 'mobile' | 'mobileSetting';
  isSelected?: boolean;
  className?: string;
}
const VARIANT_STYLES = {
  default: {
    container: 'w-full p-[8px]',
    image: 'h-[98px] w-[152px]'
  },
  panel: {
    container: 'w-[352px] py-[4px]',
    image: 'h-[84px] w-[130px]'
  },
  mobile: {
    container: 'w-full p-[4px]',
    image: 'h-[85px] w-[132px]'
  },
  mobileSetting: {
    container: 'w-full py-[4px]',
    image: 'h-[84px] w-[130px]'
  }
} as const;
export const TemplateCard = ({
  template,
  onClick,
  variant = 'default',
  isSelected = false,
  className
}: TemplateCardProps) => {
  const {
    i18n
  } = useTranslation();
  const {
    data: {
      categories
    }
  } = useSpaceCategories({
    locale: i18n.resolvedLanguage ?? 'en'
  }, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  const categoryString = useMemo(() => template.categories?.map(categoryId => categories?.find(category => category.id === categoryId)?.category ?? '')?.join(' | '), [categories, template.categories]);
  const variantStyle = VARIANT_STYLES[variant];
  return <div className={cn('flex cursor-pointer gap-[12px] rounded-[8px] hover:bg-[#E9EAF0] focus:bg-[#E9EAF0] transition-all', variantStyle.container, {
    'border-[2px] border-primary-gradient-sub': isSelected,
    'border-[2px] border-transparent': !isSelected
  }, className)} onClick={onClick} data-sentry-component="TemplateCard" data-sentry-source-file="TemplateCard.tsx">
      <div className={cn('relative shrink-0 overflow-hidden rounded-[4px]', variantStyle.image)}>
        <LazyLoadImage className="size-full rounded-2xs border border-[rgba(39,38,46,0.12)] object-cover transition-transform duration-200 hover:scale-105 active:scale-105" src={template.thumbnailUrl} alt={template.name + '_thumbnail'} data-sentry-element="LazyLoadImage" data-sentry-source-file="TemplateCard.tsx" />
        {isSelected && <>
            <div className="absolute inset-0 bg-[rgba(39,38,46,0.54)]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <CheckSmallCircleIcon className="size-2xl text-white" />
            </div>
          </>}
      </div>

      <div>
        <div className="flex h-full flex-col justify-center">
          <p className="text-[12px] font-semibold text-[#8C9097]">
            {categoryString}
          </p>
          <h3 className="text-[16px] font-medium text-default">
            {template.name}
          </h3>
        </div>
      </div>
    </div>;
};